
import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/tables/Table';
import analyticsDataFile from '../../../public/roster-analytics.json';

interface AnalyticsData {
  totalArtists: number;
  totalBookingsThisMonth: number;
  mostPopularGenre: string;
  topRatedArtist: string;
  projectedMonthlyRevenue: number;
  artistGrowthRate: number;
  sponsorConversionRate: number;
}

const metricDisplayNames: Record<string, string> = {
  totalArtists: 'Total Artists',
  totalBookingsThisMonth: 'Total Bookings (This Month)',
  mostPopularGenre: 'Most Popular Genre',
  topRatedArtist: 'Top Rated Artist',
  projectedMonthlyRevenue: 'Projected Monthly Revenue',
  artistGrowthRate: 'Artist Growth Rate',
  sponsorConversionRate: 'Sponsor Conversion Rate',
};

const formatMetricValue = (key: keyof AnalyticsData, value: number | string): string => {
  switch (key) {
    case 'projectedMonthlyRevenue':
      return `$${Number(value).toLocaleString()}`;
    case 'artistGrowthRate':
      return `+${value}%`;
    case 'sponsorConversionRate':
      return `${value}%`;
    default:
      return String(value);
  }
};

const RosterAnalyticsRealDataTable: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);
      try {
        setAnalyticsData(analyticsDataFile);
      } catch (error) {
        console.error('Error loading roster analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">Roster Analytics</h2>
      <Table className="bg-kala-900 text-white rounded-lg p-6">
        <TableHeader>
          <TableRow>
            <TableHead>Metric</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={2} className="text-center">Loading data...</TableCell>
            </TableRow>
          ) : analyticsData ? (
            (Object.keys(analyticsData) as Array<keyof AnalyticsData>).map((key) => (
              <TableRow key={key}>
                <TableCell>{metricDisplayNames[key] || key}</TableCell>
                <TableCell>{formatMetricValue(key, analyticsData[key])}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} className="text-center">No data available.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default RosterAnalyticsRealDataTable;
